import { useCallback, useEffect, useState } from "react";
import {
  generateCologuardCampaign,
  getCologuardCampaignById,
  getCologuardDashboardMetrics,
} from "../services/api.js";

const ACTIVE_STATUSES = new Set([
  "generation_requested",
  "coupon_generation_queued",
  "generation_in_process",
]);

export function isActiveCampaignStatus(status) {
  return ACTIVE_STATUSES.has(String(status || ""));
}

export default function useCampaignDetail({ campaignId, accessToken, enabled, notify }) {
  const [state, setState] = useState({ loading: true, refreshing: false, error: "", campaign: null });
  const [isRetrying, setIsRetrying] = useState(false);

  const loadCampaign = useCallback(async ({ silent = false, signal } = {}) => {
    if (!enabled || !campaignId) return null;
    setState((current) => ({
      ...current,
      loading: silent ? current.loading : true,
      refreshing: silent,
      error: "",
    }));
    try {
      const campaign = await getCologuardCampaignById({ campaignId, accessToken, signal });
      let campaignWithMetrics = campaign;
      if (campaign.campaign_designator) {
        try {
          const metrics = await getCologuardDashboardMetrics({
            campaignDesignators: [campaign.campaign_designator],
            accessToken,
            signal,
          });
          campaignWithMetrics = {
            ...campaign,
            inventory: metrics.campaigns?.[0] || null,
          };
        } catch (metricsError) {
          if (metricsError?.name === "AbortError") return null;
          console.warn("Campaign performance metrics could not be loaded", metricsError);
        }
      }
      setState({ loading: false, refreshing: false, error: "", campaign: campaignWithMetrics });
      return campaignWithMetrics;
    } catch (error) {
      if (error?.name === "AbortError") return null;
      setState((current) => ({ ...current, loading: false, refreshing: false, error: error.message }));
      return null;
    }
  }, [accessToken, campaignId, enabled]);

  useEffect(() => {
    const controller = new AbortController();
    loadCampaign({ signal: controller.signal });
    return () => controller.abort();
  }, [loadCampaign]);

  useEffect(() => {
    const status = String(state.campaign?.campaign_status || "");
    if (!isActiveCampaignStatus(status)) return undefined;
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "visible") loadCampaign({ silent: true });
    }, 7000);
    return () => window.clearInterval(intervalId);
  }, [loadCampaign, state.campaign?.campaign_status]);

  const retryCampaign = useCallback(async () => {
    if (!campaignId) return;
    setIsRetrying(true);
    try {
      const result = await generateCologuardCampaign(
        {
          campaign_id: campaignId,
          force_recreate_rule: false,
          retry_failed_batches: true,
        },
        { accessToken }
      );
      await loadCampaign({ silent: true });
      notify(result.message, "success");
    } catch (error) {
      notify(error.message, "error");
      await loadCampaign({ silent: true });
    } finally {
      setIsRetrying(false);
    }
  }, [accessToken, campaignId, loadCampaign, notify]);

  return { ...state, isRetrying, loadCampaign, retryCampaign };
}
